<template>
	<v-row align="center" justify="center">
		<v-col v-if="!sent">
			<v-form type="POST" @submit.prevent="send" ref="form">
				<v-card width="400px" class="mx-auto px-7 py-7">
					<v-card-title>Reset password</v-card-title>

					<v-card-text class="mx-1">
						Enter the email address associated with your account and a new password.
					</v-card-text>

					<v-text-field
						v-model="email"
						:rules="emailRules"
						label="Email adress"
						required
						outlined
						hide-details="auto"
						class="mx-4 pt-5 pb-6"
					/>
					<v-text-field
						v-model="newPassword"
						:rules="newPasswordRules"
						:append-icon="show.newPassword ? 'mdi-eye' : 'mdi-eye-off'"
						:type="show.newPassword ? 'text' : 'password'"
						@click:append="show.newPassword = !show.newPassword"
						label="New password"
						required
						outlined
						error-count="6"
						hide-details="auto"
						class="mx-4 pb-2"
					/>
					<v-text-field
						v-model="newPasswordConfirmation"
						:rules="newPasswordConfirmationRules.concat(passwordMatch)"
						:append-icon="show.newPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off'"
						:type="show.newPasswordConfirmation ? 'text' : 'password'"
						@click:append="show.newPasswordConfirmation = !show.newPasswordConfirmation"
						label="Confirm new password"
						required
						outlined
						hide-details="auto"
						class="mx-4 pb-2"
					/>

					<v-card-actions class="mx-2">
						<v-btn block color="primary" type="submit" :loading="isLoading">Send</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-col>

		<v-col v-else>
			<v-card width="400px" class="mx-auto px-7 py-7">
				<v-card-text class="text-center" v-html="responseText" />
				<v-card-actions class="mx-2">
					<v-btn @click="redirect" block color="primary">Go back to login</v-btn>
				</v-card-actions>
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
	import UserAPI from '@/api/user.js';

	/**
	 * @module ResetPasswordView
	 *
	 * @vue-data {boolean} [sent=false] - Form has been submitted
	 * @vue-data {string} [responseText=''] - Display response text if there is any
	 * @vue-data {boolean} [isLoading=false] - Display progress on button
	 * @vue-data {string} [token=''] - Get the URL token param
	 * @vue-data {string} [email=''] - The user email
	 * @vue-data {string} [newPassword=''] - The new user password
	 * @vue-data {string} [newPasswordConfirmation=''] - Confirm new user password
	 * @vue-data {array<boolean>} [emailRules=true]= - Rules for email validation
	 * @vue-data {array<boolean>} [newPasswordRules=true]= - Rules for new password validation
	 * @vue-data {array<boolean>} [newPasswordConfirmationRules=true]= - Rules for new password confirmation validation
	 * @vue-data {object} [show={}] - Show new password and new password confirmation text
	 * @vue-data {boolean} [show.newPassword=false] - Show new password text
	 * @vue-data {boolean} [show.newPasswordConfirmation=false] - Show new password confirmation text
	 *
	 * @vue-computed {string} passwordMatch - New password must match the confirmed new password
	 */

	export default {
		data() {
			return {
				sent: false,
				responseText: '',
				isLoading: false,
				token: this.$route.params.token || '',
				email: '',
				newPassword: '',
				newPasswordConfirmation: '',
				emailRules: [],
				newPasswordRules: [
					v => !!v || 'New password is required.',
					v => /(?=.*\d)/.test(v) || 'New password must contain at least 1 number.',
					v => (v && v.length >= 8) || 'New password must contain at least 8 characters.',
					v => /(?=.*[A-Z])/.test(v) || 'New password must contain at least 1 uppercase letter.',
					v => /(?=.*[a-z])/.test(v) || 'New password must contain at least 1 lowercase letter.',
					v =>
						/([!@#$%^&*-])/.test(v) ||
						'New password must contain at least 1 special character [!@#$%^&*-].',
				],
				newPasswordConfirmationRules: [v => !!v || 'Confirm new password is required.'],
				show: {
					newPassword: false,
					newPasswordConfirmation: false,
				},
			};
		},
		computed: {
			passwordMatch() {
				return this.newPassword === this.newPasswordConfirmation || 'Password must match';
			},
		},
		methods: {
			/**
			 * @async
			 *
			 * @returns {void}
			 *
			 * @description
			 * - Set email validation rules
			 * - Validate form
			 * <br>&nbsp;
			 * - @HTTP 200 & @HTTP 500
			 * - Display progress on button
			 * - Display corresponding message based on status code 
			 */
			async send() {
				this.emailRules = [
					v => !!v || 'The email adress is required.',
					v => /.+@.+\..+/.test(v) || 'The email adress must be valid.',
				];

				if (this.$refs.form.validate()) {
					if (!this.email.trim()) return;

					const data = {
						token: this.token,
						email: this.email,
						password: this.newPassword,
						password_confirmation: this.newPasswordConfirmation,
					};

					try {
						this.isLoading = true;

						const response = await UserAPI.resetPassword(data);

						if (response.status === 200) {
							this.responseText = response.data.statusText;
							this.sent = true;
						}
					} catch (error) {
						if (error.response.status === 500) {
							this.responseText = error.response.data.statusText;
							this.sent = true;
						}
					} finally {
						this.isLoading = false;
					}
				}
			},

			/**
			 * Redirect user to Login page
			 *
			 * @returns {void}
			 */
			redirect() {
				this.$router.push({ name: 'Login' });
			},
		},
	};
</script>
